import {TDocumentType} from "../types/types";
import {StringUtil} from "./Utils";

export const ValidateUtil = {
    cnj(value: string): boolean {
        return ValidatorFactory.getValidator('PROCESSO').isValid(value);
    },
    cpf(value: string): boolean {
        return ValidatorFactory.getValidator('CPF').isValid(value);
    },
    cnpj(value: string): boolean {
        return ValidatorFactory.getValidator('CNPJ').isValid(value);
    },
    cnpjBase(value: string): boolean {
        return ValidatorFactory.getValidator('RADICAL_CNPJ').isValid(value);
    }
}

class ValidatorFactory {
    static getValidator(type: TDocumentType): BaseValidator {
        switch (type) {
            case 'CPF':
                return new CPFValidator();
            case 'CNPJ':
                return new CNPJValidator();
            case 'RADICAL_CNPJ':
                return new CNPJBaseNumberValidator();
            case 'PROCESSO':
                return new CNJValidator();
            default:
                throw new Error('Validator type not recognized');
        }
    }
}

abstract class BaseValidator {
    protected abstract calculateAndCompareDigits(value: string): boolean;

    protected clean(value: string): string {
        return StringUtil.extractDigits(value);
    }

    public isValid(value: string): boolean {
        if (typeof value !== 'string' || !value.trim()) {
            return false;
        }

        const unmaskedValue = this.clean(value);
        if (!unmaskedValue || this.isEqualDigitSequence(unmaskedValue)) {
            return false;
        }

        return this.calculateAndCompareDigits(unmaskedValue);
    }

    private isEqualDigitSequence(value: string): boolean {
        return /^(.)\1*$/.test(value);
    }

    protected checksumForMod97(value: string, partSize: number): string {
        let partialResult = 0;
        for (let i = 0; i < value.length; i += partSize) {
            const part = value.substring(i, i + partSize);
            partialResult = (parseInt(partialResult + part) % 97);
        }
        return (98 - partialResult).toString().padStart(2, '0');
    }

    protected checksumForMod11(value: string, weights: number[]): string {
        let sum = 0;
        for (let i = 0; i < value.length; i++) {
            // Regra ASCII da Receita Federal
            const numericValue = value.charCodeAt(i) - 48;
            sum += numericValue * weights[i];
        }
        const checksum = 11 - (sum % 11);
        return (checksum >= 10 ? 0 : checksum).toString();
    }
}

class CPFValidator extends BaseValidator {
    protected calculateAndCompareDigits(cpf: string): boolean {
        if (cpf.length !== 11) {
            return false;
        }
        const valueForCalculation = StringUtil.firstDigits(cpf, 9);
        const weights = [11, 10, 9, 8, 7, 6, 5, 4, 3, 2];
        const d1 = this.checksumForMod11(valueForCalculation, weights.slice(1));
        const d2 = this.checksumForMod11(valueForCalculation + d1, weights.slice());
        return StringUtil.lastDigits(cpf, 2) === d1 + d2;
    }
}

class CNPJValidator extends BaseValidator {
    // Permite letras e força maiúsculas
    protected clean(value: string): string {
        if (!value) return "";
        return value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
    }

    protected calculateAndCompareDigits(cnpj: string): boolean {
        if (cnpj.length !== 14) {
            return false;
        }

        const valueForCalculation = cnpj.substring(0, 12);
        const weights = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
        
        const d1 = this.checksumForMod11(valueForCalculation, weights.slice(1));
        const d2 = this.checksumForMod11(valueForCalculation + d1, weights.slice());

        return cnpj.substring(12, 14) === d1 + d2;
    }
}

class CNPJBaseNumberValidator extends BaseValidator {
    // Permite letras e força maiúsculas
    protected clean(value: string): string {
        if (!value) return "";
        return value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
    }

    protected calculateAndCompareDigits(raizCnpj: string): boolean {
        return raizCnpj.length === 8;
    }
}

class CNJValidator extends BaseValidator {
    // Restaurando a lógica original do Processo Judicial que havia sido apagada
    protected calculateAndCompareDigits(processo: string): boolean {
        if (processo.length !== 20) {
            return false;
        }
        const partSize = 7;
        const valueForCalculation = processo.substring(0, 7) + processo.substring(9) + '00';
        return processo.substring(7, 9) === this.checksumForMod97(valueForCalculation, partSize);
    }
}